import { useState } from 'react';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Table, Tabs } from '@frontend/design-system';
import { getPlatformDisplayName, getPlatformColor } from './downloadHelpers';

interface ReleaseTableValue {
  version: string;
  assetCount: number;
  published: Date;
  tags: ('darwin' | 'win32' | 'linux')[];
  downloadLinks: {
    linux?: string;
    mac?: string;
    win32?: string;
  };
}

const DEFAULT_TAB = 'prod-tab';

export const ReleaseList = ({
  onReleaseDetailsClick,
  handleDownload,
  data,
}: {
  onReleaseDetailsClick?: (version: string) => void;
  handleDownload: (platform: 'mac' | 'win32' | 'linux', links: any) => void;
  data: ReleaseTableValue[];
}) => {
  const [activeTab, setActiveTab] = useState<string>(DEFAULT_TAB);

  /* TODO:*** This needs to be wired up */
  // const currentTag = activeTab === 'dev-tab' ? 'dev,preview' : 'prod';

  return (
    <div>
      <Tabs initialTab={activeTab} onChange={setActiveTab}>
        <Tabs.Bar fullWidth css={{ marginBottom: theme.spacing(2) }}>
          <Tabs.Tab id='prod-tab'>Production</Tabs.Tab>
          <Tabs.Tab id='dev-tab'>Development</Tabs.Tab>
        </Tabs.Bar>
        <Tabs.Panel controller='prod-tab'>
          {/* TODO: Wire up the production tab */}
          <ReleaseTable releases={data} onReleaseDetailsClick={onReleaseDetailsClick} handleDownload={handleDownload} />
        </Tabs.Panel>
        {/* TODO: Wire up the development tab right now all dev */}
        <Tabs.Panel controller='dev-tab'>
          <ReleaseTable releases={data} onReleaseDetailsClick={onReleaseDetailsClick} handleDownload={handleDownload} />
        </Tabs.Panel>
      </Tabs>
    </div>
  );
};

type Props = {
  releases: ReleaseTableValue[];
  onReleaseDetailsClick?: (version: string) => void;
  handleDownload: (platform: 'mac' | 'win32' | 'linux', links: any) => void;
};

const ReleaseTable = ({ releases, onReleaseDetailsClick, handleDownload }: Props) => {
  return (
    <Table
      data={releases ?? []}
      colConfig={[
        {
          Header: 'Version',
          headerAlign: 'center',
          id: 'version',
          accessor: (release) => <div style={{ textAlign: 'center' }}>{release.version || 'N/A'}</div>,
          minWidth: 50,
          maxWidth: 130,
        },
        {
          Header: 'Published At',
          headerAlign: 'center',
          id: 'publishedAt',
          accessor: (release) => (
            <div style={{ textAlign: 'center', width: '100%' }}>
              {!release.published ? 'N/A' : new Date(release.published).toLocaleString()}
            </div>
          ),
          minWidth: 100,
        },
        {
          Header: 'Release Tags',
          headerAlign: 'center',
          id: 'tags',
          accessor: (release) => <div style={{ textAlign: 'center' }}>{release.tags?.join(', ') || 'N/A'}</div>,
          minWidth: 100,
        },
        {
          Header: 'Download Links',
          headerAlign: 'center',
          id: 'downloadLinks',
          accessor: (release) => {
            const platforms: Array<'mac' | 'win32' | 'linux'> = ['mac', 'win32', 'linux'];

            return (
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
                {platforms.map((platform) => {
                  if (!release.downloadLinks?.[platform]) return null;

                  const colors = getPlatformColor(platform);
                  const displayName = getPlatformDisplayName(platform);

                  return (
                    <button
                      key={platform}
                      onClick={() => handleDownload(platform, release.downloadLinks)}
                      style={{
                        ...colors,
                        padding: '4px 8px',
                        borderRadius: '4px',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '12px',
                      }}
                    >
                      {displayName}
                    </button>
                  );
                })}
              </div>
            );
          },
          minWidth: 100,
        },
        {
          Header: 'Asset Count',
          headerAlign: 'center',
          id: 'assetCount',
          accessor: (release) => <div style={{ textAlign: 'center' }}>{release.assetCount}</div>,
          minWidth: 50,
          maxWidth: 130,
        },
      ]}
      rowActions={{
        actions: [
          {
            Icon: () => <Icon name='arrow-right-small' />,
            label: 'View Details',
            onClick: (release: ReleaseTableValue) => {
              if (release.version && onReleaseDetailsClick) {
                onReleaseDetailsClick(release.version);
              }
            },
          },
        ],
      }}
      isPaginated
      hasGlobalSearch
      tableActions={[
        {
          Icon: () => <Icon name='update' />,
          label: 'Refresh',
          onClick: () => {
            window.location.reload();
          },
        },
      ]}
    />
  );
};
